import { useState } from 'react';
import { Search, BarChart2, Briefcase, Sparkles } from 'lucide-react';
import { SUGGESTED_QUERIES } from '../data/suggestedQueries';
import type { SuggestedQuery, SuggestedQueryCategory } from '../types/chat.types';

interface CategoryMeta {
  category: SuggestedQueryCategory;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const PANEL_CATEGORIES: CategoryMeta[] = [
  { category: 'research',       label: 'Research',       icon: Search },
  { category: 'analyze',        label: 'Analyze',        icon: BarChart2 },
  { category: 'sales_tools',    label: 'Sales Tools',    icon: Briefcase },
  { category: 'latest_studies', label: 'Latest Studies', icon: Sparkles },
];

interface CategoryQueriesProps {
  onSelect: (query: SuggestedQuery) => void;
}

/**
 * The small pill bar shown under the welcome-screen input: a row of tiny
 * category tabs that expand — on demand, one at a time — into a compact list
 * of ready-made queries. Gated end-to-end by `FEATURES.ONBOARDING_GUIDANCE`
 * in the parent, so it can be switched off from the code with one boolean.
 */
export const CategoryQueries = ({ onSelect }: CategoryQueriesProps) => {
  const [activeCategory, setActiveCategory] = useState<SuggestedQueryCategory | null>(null);

  const handleTabClick = (category: SuggestedQueryCategory) => {
    setActiveCategory((prev) => (prev === category ? null : category));
  };

  const handleSelect = (query: SuggestedQuery) => {
    onSelect(query);
    setActiveCategory(null);
  };

  const activeQueries = activeCategory
    ? SUGGESTED_QUERIES.filter((q) => q.category === activeCategory)
    : [];

  return (
    <div className="w-full">
      <div
        data-tutorial="category-buttons"
        className="flex flex-wrap gap-1.5 justify-center"
      >
        {PANEL_CATEGORIES.map(({ category, label, icon: Icon }) => (
          <button
            key={category}
            onClick={() => handleTabClick(category)}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-sm font-medium transition-colors cursor-pointer
              ${activeCategory === category
                ? 'bg-slate-100 border-slate-300 text-slate-700'
                : 'border-slate-200 text-slate-400 hover:bg-slate-50 hover:border-slate-300 hover:text-slate-600'
              }`}
          >
            <Icon className="w-3 h-3" />
            {label}
          </button>
        ))}
      </div>

      {activeCategory && (
        <div className="mt-2 rounded-lg border border-slate-200 overflow-hidden bg-white">
          {activeQueries.map((query, idx) => (
            <button
              key={query.id}
              onClick={() => handleSelect(query)}
              className={`w-full text-left px-4 py-3 text-sm text-slate-600 hover:bg-slate-50 transition-colors cursor-pointer
                ${idx < activeQueries.length - 1 ? 'border-b border-slate-100' : ''}`}
            >
              {query.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
